import type { FileInfo } from './File'
import { PermissionId } from './Permission'
import { SerializedToken } from '../common/token'
import { IntegrationServiceId } from '../common/integrations'
import { UserConsentState } from '../components/Consent/model'

export interface RoleDetails {
  id: string
  name: string
  metadata?: string
}

export enum UserOwnerType {
  USER = 'user',
  CURRENT_USER = 'current-user',
}

export interface UserOwner {
  ownerType: UserOwnerType
  ownerId: string
}

export interface ExternalInfo {
  serviceId: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  contactValues: { [key: string]: any }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  contactSchema: { [key: string]: any }
}

export interface User {
  id: string
  accountId: string
  firstName: string
  lastName: string
  username: string
  name: string
  email: string
  permissions?: Array<PermissionId>
  phoneNumber: string
  roles: Array<RoleDetails>
  type?: string
  identities: Array<UserIdentity>
  createdAt?: string
  updatedAt?: string
  status?: string
  integrations?: Array<ExternalUserServiceIntegration>
  integrationConfig?: IntegrationConfig
  settings?: UserSettings
  effectiveSettings?: UserSettings
  enterpriseSettings?: EnterpriseSettings
  externalInfos: ExternalInfo[]
  metadata?: string
  owner?: UserOwner | null
}

export interface Owner {
  firstName: string
  lastName: string
  name: string
  ownerId: string
}

export interface Team {
  id: string
  name: string
  members?: Array<User>
  integrations?: Array<ExternalTeamServiceIntegration>
}

export const SERVICE_ID_TO_INTEGRATION_TYPE_MAPPING: { [key in IntegrationServiceId]?: string } = {
  [IntegrationServiceId.ZOOM]: 'meeting_provider',
  [IntegrationServiceId.GOOGLE_CALENDAR]: 'calendar',
  [IntegrationServiceId.EXCHANGE_CALENDAR]: 'calendar',
  [IntegrationServiceId.OUTLOOK_CALENDAR]: 'calendar',
  [IntegrationServiceId.WEALTHBOX]: 'crm',
  [IntegrationServiceId.SALESFORCE]: 'crm',
  [IntegrationServiceId.PRACTIFI]: 'crm',
  [IntegrationServiceId.XLR8]: 'crm',
  [IntegrationServiceId.REDTAIL]: 'crm',
  [IntegrationServiceId.PHONE]: 'meeting_provider',
  [IntegrationServiceId.GMAIL]: 'mail',
  [IntegrationServiceId.OUTLOOK_MAIL]: 'mail',
  [IntegrationServiceId.ZOHO]: 'crm',
  [IntegrationServiceId.HUBSPOT]: 'crm',
  [IntegrationServiceId.DYNAMICS]: 'crm',
  [IntegrationServiceId.SMARTOFFICE]: 'crm',
  [IntegrationServiceId.E_MONEY]: 'planning',
  [IntegrationServiceId.ORION]: 'portfolio',
  [IntegrationServiceId.BLACK_DIAMOND_WP]: 'portfolio',
  [IntegrationServiceId.PRECISE_FP]: 'crm',
  [IntegrationServiceId.ELEMENTS]: 'crm',
  [IntegrationServiceId.BLACK_DIAMOND]: 'crm',
  [IntegrationServiceId.WEBEX]: 'meeting_provider',
  [IntegrationServiceId.ENGAGE]: 'crm',
  [IntegrationServiceId.ADVISOR_ENGINE]: 'crm',
  [IntegrationServiceId.INVESTCLOUD]: 'crm',
  [IntegrationServiceId.WOVE]: 'portfolio',
  [IntegrationServiceId.RINGCENTRAL]: 'meeting_provider',
  [IntegrationServiceId.CONQUEST_PLANNING]: 'planning',
  [IntegrationServiceId.BOX]: 'document',
  [IntegrationServiceId.SHAREPOINT]: 'document',
}

export interface PhoneRecordingConfiguration {
  enabled: boolean
}

export interface ExternalUserServiceIntegration {
  id: string
  serviceId: IntegrationServiceId
  externalId: string
  externalAccountId: string
  botName?: string
  configuration: ExternalUserServiceIntegrationConfig
  phoneRecordingConfiguration?: PhoneRecordingConfiguration
  type: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  serviceConfig?: { [key: string]: any }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  metadata?: { [key: string]: any }
  state?: string
  email?: string
}

export interface ServiceConfig {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any
}

export interface ExternalUserServiceIntegrationUpdate {
  botName?: string
  configuration?: ExternalUserServiceIntegrationConfig
  phoneRecordingConfiguration?: PhoneRecordingConfiguration
  serviceConfig?: ServiceConfig
}

export interface ExternalTeamServiceIntegration {
  id: string
  serviceId: string
  externalId: string
  type: string
}

export interface ExternalUserServiceIntegrationBotConfig {
  type: 'bot'
  joinMeeting: 'hosted' | 'all'
  autoJoin?: boolean
  enforcedAutoJoin?: boolean
  strictJoinEnabled?: boolean | null
}

export interface ExternalUserServiceIntegrationRecordingConfig {
  type: 'recording'
  joinMeeting: 'hosted'
  removeSessionWhenNoRecording?: boolean
}

export type ExternalUserServiceIntegrationConfig = ExternalUserServiceIntegrationBotConfig | ExternalUserServiceIntegrationRecordingConfig

export interface AuthenticatedUser extends User {
  displayName: string
  isGhosted?: boolean
  isSystemGhost?: boolean
  demoMode?: boolean
  ghostedBy?: string
  isCrossAccountUser?: boolean
}

export interface AuthenticatedUserTokenDetails {
  tokenExpiration: number
  token: SerializedToken
}

export interface IntegrationConfig {
  botName: string
}

export type UserLanguage = 'en' | 'es'

export const USER_PRONOUNS = ['he/him', 'she/her', 'they/them'] as const

export type UserPronouns = (typeof USER_PRONOUNS)[number]

export interface AvatarConfig {
  avatarFile?: FileInfo
}

export interface UserAssets {
  avatar?: AvatarConfig | null
}

export interface UserSettings {
  sessionSharingEnabled?: boolean
  contactSharingEnabled?: boolean
  documentSharingEnabled?: boolean
  schedulingSharingEnabled?: boolean
  aiMeetingTemplateCalculatorOverride?: boolean | null
  emailSignature?: string | null
  language?: UserLanguage | null
  timezone?: string | null
  preferredPronouns?: UserPronouns | null
  assets?: UserAssets | null
}

export interface EnterpriseSettings {
  sessionSharingEnabled?: boolean
  contactSharingEnabled?: boolean
  documentSharingEnabled?: boolean
  schedulingSharingEnabled?: boolean
}

export interface UserCreateUpdateIntegrations {
  id?: string
  type: string
  serviceId: string
  externalId: string
  externalAccountId: string
}

export interface PhoneLocationInfo {
  phoneNumber: string
}

export interface ServiceLocation {
  id: string
  displayName: string
  locationDetails: PhoneLocationInfo
}

export type UserCreateUpdatePayload = {
  firstName?: string
  lastName?: string
  username?: string
  name?: string
  email?: string
  phoneNumber?: string
  roleIds?: Array<string>
  isConsoleUser?: boolean
  integrationConfig?: IntegrationConfig
  integrations?: Array<UserCreateUpdateIntegrations>
  settings?: UserSettings
  identity?: UserIdentity
}

export interface UserPromotePayload {
  firstName: string
  lastName: string
  name: string
  email: string
  identity: UserIdentity
  roleIds?: Array<string>
  integrations?: Array<UserCreateUpdateIntegrations>
  owner?: UserOwner
}

export type ExternalUserCreateUpdatePayload = UserCreateUpdatePayload & {
  referenceUserId: string | null
  owner: UserOwner | null
}

export interface Me extends User {
  me: string
}

export type AddRemoveUserRolesPayload = {
  roleIds?: Array<string>
}

export type UserDetails = {
  userId: string
} & Omit<UserCreateUpdatePayload, 'phoneNumber' | 'username'>

export type UserIdentity = {
  type: 'managed' | 'anonymous' | 'external'
  location: string
}

export type UserIntegrationCreatePayload = {
  code: string
  redirectUri: string
}

export type ShareType = 'meeting' | 'contact' | 'email' | 'document' | 'scheduling'
export type ConsentType = 'initial' | 'automatic' | 'manual'

export interface UserShareSettingsUpdatePayload {
  sharedWithUserIds: Array<string>
  shareType: ShareType
}

export interface UserShareSettings {
  id: string
  ownerUserId: string
  sharedWithUserId: string
  shareType: ShareType
}

export interface ExternalUserConsentChange {
  id: string
  userId: string
  roomId?: string
  sessionId?: string
  oldValue?: UserConsentState
  newValue?: UserConsentState
  changedByUserId: string
  reason?: string
  attachmentFileId?: string
  changedAt: string
  type: ConsentType
  deletedBy: string
  deletedAt: string
}

export interface Identity {
  type: string
  id: string
}
